import { Camera, RefreshCw } from 'lucide-react';
import type { CleaningPhotoResponse, DamageReportResponse } from '@/shared/api';
import { formatDate } from '@/shared/lib/format';
import { PhotoThumbnail } from '../PhotoThumbnail';
import { CameraCaptureButton } from '../capture/CameraCaptureButton';
import { DAMAGE_SEVERITY_LABEL } from '../cleaningLabels';

interface Props {
  taskId: string;
  report: DamageReportResponse;
  photos: CleaningPhotoResponse[];
  canWork: boolean;
  canAcknowledge: boolean;
  onAcknowledge: (reportId: string) => void;
  onCapture: (reportId: string, file: File) => void;
  onRetake: (photoId: string) => void;
  attachingReportId?: string;
  retakingPhotoId?: string;
}

/** Eine Schaden-/Fehlt-Meldung mit Live-Foto-Capture, Thumbnails, Retake und Acknowledge. */
export function ReportCard({
  taskId,
  report,
  photos,
  canWork,
  canAcknowledge,
  onAcknowledge,
  onCapture,
  onRetake,
  attachingReportId,
  retakingPhotoId,
}: Props) {
  return (
    <li className="border-line/60 space-y-2 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-ink truncate font-medium">{report.title}</div>
          <p className="text-ink-soft text-sm">{report.description}</p>
        </div>
        {report.kind === 'Damage' && (
          <span className="text-ink-soft shrink-0 text-xs">
            {DAMAGE_SEVERITY_LABEL[report.severity]}
            {report.estimatedCost !== null ? ` · EUR ${report.estimatedCost.toFixed(2)}` : ''}
          </span>
        )}
      </div>

      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <div key={p.id} className="space-y-1">
              <PhotoThumbnail taskId={taskId} photo={p} label={`Beleg zu „${report.title}"`} />
              {canWork && (
                <button
                  type="button"
                  disabled={retakingPhotoId === p.id}
                  onClick={() => onRetake(p.id)}
                  className="text-ink-soft hover:text-sealwax inline-flex items-center gap-1 text-xs disabled:opacity-50"
                >
                  <RefreshCw size={12} /> Neu
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-xs">
        {canWork && (
          <CameraCaptureButton
            onCapture={(file) => onCapture(report.id, file)}
            disabled={attachingReportId === report.id}
            className="text-sage hover:text-sage/80 inline-flex items-center gap-1"
          >
            <Camera size={13} /> Foto aufnehmen
          </CameraCaptureButton>
        )}
        {report.acknowledgedAt ? (
          <span className="text-ink-soft">Bestätigt {formatDate(report.acknowledgedAt)}</span>
        ) : canAcknowledge ? (
          <button
            type="button"
            onClick={() => onAcknowledge(report.id)}
            className="text-sage inline-flex min-h-11 items-center hover:underline"
          >
            Bestätigen
          </button>
        ) : (
          <span className="text-ink-soft">Offen</span>
        )}
      </div>
    </li>
  );
}
